import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import CurrencyInput from "~/components/formElements/CurrencyInput.tsx";
import DatePickerInput from "~/components/formElements/DatePickerInput.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint.tsx";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input.tsx";
import PaymentTargetDaysInput from "~/components/formElements/PaymentTargetInput/PaymentTargetInput.tsx";
import Select from "~/components/formElements/Select.tsx";
import SubmitButton from "~/components/formElements/SubmitButton.tsx";
import Textarea from "~/components/formElements/Textarea.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { useAuth } from "~/contexts/AuthContext";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import BillingInvoiceRecipientFormSection
	from "~/modules/billing/components/formSections/BillingInvoiceRecipientFormSection.tsx";
import { DEFAULT_PAYMENT_TARGET_DAYS } from "~/modules/billing/constants/billingConstants.ts";
import {
	useCompaniesCentralInvoiceRecipientsSelectOptions,
} from "~/modules/client/hooks/useCompaniesCentralInvoiceRecipientsSelectOptions.ts";
import {
	useCompaniesContactPersonSelectOptions,
} from "~/modules/client/hooks/useCompaniesContactPersonSelectOptions.ts";
import { createQuota } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import type { CreateAdvanceQuotaData, CreateQuotaConsumerData, Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import QuotaConsumerSelector from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaConsumerSelector.tsx";
import QuotaConsumersFormSection from "~/modules/quota/components/formSections/QuotaConsumersFormSection.tsx";
import QuotaCoreDetailsFormSection from "~/modules/quota/components/formSections/QuotaCoreDetailsFormSection.tsx";
import { useQuotaConsumerOrderInfo } from "~/modules/quota/hooks/useQuotaConsumerOrderInfo.ts";
import {
	getConsumerProjectIds,
	getCreateAdvanceQuotaSchema,
	prepareCreateAdvanceQuotaPayload,
} from "~/modules/quota/utils/quotaFormUtils.ts";
import type { FormInputOption } from "~/types/form.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

// The advance quota form. Its `consumers` field holds the assigned orders, so the
// field array lives in the form state instead of a separate React state.
export type CreateAdvanceQuotaFormData = Omit<
	CreateAdvanceQuotaData,
	"invoiceDeliveryEndDate" | "invoiceDeliveryStartDate" | "usableFrom" | "usableUntil" | "consumers"
> & {
	invoiceDeliveryStartDate: Date;
	invoiceDeliveryEndDate: Date;
	usableFrom?: Date | null;
	usableUntil?: Date | null;
	paymentTargetDaysCustom?: number | null;
	consumers: CreateQuotaConsumerData[];
}

type Props = {
	companyId: string;
	onCancel: () => void;
	// The created quota and the distinct project ids of its assigned consumers.
	onSuccess: (quota: Quota, projectIds: string[]) => void;
};

export const CreateAdvanceQuotaForm = ({
	companyId,
	onCancel,
	onSuccess,
}: Props) => {
	const { user } = useAuth();
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [showOrderSelect, setShowOrderSelect] = useState<boolean>(false);

	const companiesContactPersonSelectOptions = useCompaniesContactPersonSelectOptions(companyId);
	const companiesInvoiceRecipientSelectOptions = useCompaniesCentralInvoiceRecipientsSelectOptions(companyId);

	const orderInfoById = useQuotaConsumerOrderInfo(companyId);

	const schema = useMemo(() => getCreateAdvanceQuotaSchema(orderInfoById), [orderInfoById]);

	const defaultValues = useMemo(() => {
		return {
			clientContactPersonId: null,
			amountCents: 0,
			invoiceDeliveryStartDate: undefined,
			invoiceDeliveryEndDate: undefined,
			invoiceRecipientId: null,
			invoiceProjectId: null,
			managedBy: user?.id ?? "",
			comment: "",
			clientContactPerson: "",
			orderNumber: "",
			paymentTargetDays: DEFAULT_PAYMENT_TARGET_DAYS,
			paymentTargetDaysCustom: null,
			consumers: [],
			title: "",
			usableFrom: null,
			usableUntil: null,
		};
	}, [user?.id]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid, errors },
		watch,
		setValue,
		trigger,
	} = useForm<CreateAdvanceQuotaFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateAdvanceQuotaFormData>(schema),
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "consumers",
	});


	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;
	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			void trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	// The consumer budgets and the total feed cross-field rules. A change to one
	// field does not re-validate the others on its own. After the first submit,
	// re-validate the affected fields when a consumer or the total changes.
	// Name the nested consumer fields one by one. A no-argument trigger() clears
	// a top-level error, but it does not clear an error on a field-array path.
	const watchedConsumers = useWatch({ control, name: "consumers" });
	const watchedAmountCents = useWatch({ control, name: "amountCents" });

	useEffect(() => {
		if (!isSubmitted) {
			return;
		}
		const consumerBudgetFields = (watchedConsumers ?? []).map(
			(_, index) => `consumers.${index}.budgetCentsAssigned` as const,
		);
		void trigger(["amountCents", ...consumerBudgetFields]);
	}, [watchedConsumers, watchedAmountCents, isSubmitted, trigger]);

	// The consumers can belong to several projects. The invoice must target one project.
	// Collect the distinct projects of the current consumers as select options.
	const invoiceProjectOptions = useMemo<FormInputOption[]>(() => {
		const titleByProjectId = new Map<string, string>();
		(watchedConsumers ?? []).forEach((consumer) => {
			const info = orderInfoById[consumer.orderId];
			if (info) {
				titleByProjectId.set(info.projectId, info.projectTitle);
			}
		});
		return Array.from(titleByProjectId, ([value, label]) => ({ value, label }));
	}, [watchedConsumers, orderInfoById]);

	// The extra project field appears only when the consumers span more than one project.
	const requiresInvoiceProject = invoiceProjectOptions.length > 1;

	const invoiceProjectId = watch("invoiceProjectId");

	// Keep the invoice project value valid. Clear it when the field is not required.
	// Also clear it when the chosen project is no longer among the consumers.
	useEffect(() => {
		if (!invoiceProjectId) {
			return;
		}
		const isStillSelectable = requiresInvoiceProject
			&& invoiceProjectOptions.some((option) => option.value === invoiceProjectId);
		if (!isStillSelectable) {
			setValue("invoiceProjectId", null);
		}
	}, [invoiceProjectId, invoiceProjectOptions, requiresInvoiceProject, setValue]);

	// The requirement depends on the consumer projects. Re-validate the field after the
	// first submit when that requirement changes.
	useEffect(() => {
		if (!isSubmitted) {
			return;
		}
		void trigger("invoiceProjectId");
	}, [isSubmitted, requiresInvoiceProject, trigger]);

	const usableFrom = watch("usableFrom");
	const invoiceDeliveryStartDate = watch("invoiceDeliveryStartDate");

	useEffect(() => {
		if (!isSubmitted) return;
		void trigger("usableUntil");
	}, [isSubmitted, trigger, usableFrom]);

	useEffect(() => {
		if (!isSubmitted) return;
		void trigger("invoiceDeliveryEndDate");
	}, [trigger, invoiceDeliveryStartDate, isSubmitted]);

	// The selector confirms one or more orders. Append each to the field array, then close it.
	const handleConfirmOrders = useCallback((consumers: CreateQuotaConsumerData[]) => {
		consumers.forEach(consumer => append(consumer));
		setShowOrderSelect(false);
	}, [append]);

	const onSubmit = useCallback(async (data: CreateAdvanceQuotaFormData) => {
		try {
			setIsBusy(true);

			const quotaData = prepareCreateAdvanceQuotaPayload(data);
			const createdQuota = await createQuota(companyId, quotaData).then(getDataFromResponse);
			onSuccess(createdQuota, getConsumerProjectIds(data.consumers, orderInfoById));
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}

	}, [companyId, onSuccess, orderInfoById]);


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			{showOrderSelect && <QuotaConsumerSelector companyId={companyId}
													   excludedOrderIds={fields.map(field => field.orderId)}
													   onCancel={() => setShowOrderSelect(false)}
													   onConfirm={handleConfirmOrders} />}
			{!showOrderSelect && <>
				<SidebarHeader>
					<SidebarHeaderHeadline>Neues Abrechnungskontingent</SidebarHeaderHeadline>
				</SidebarHeader>
				<SidebarContent>
					{isBusy && <SidebarBusyOverlay />}
					{!!serverErrorMessage &&
						<SidebarErrorOverlay title="Erstellung fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
					<FormSectionGroup>
						<QuotaCoreDetailsFormSection
							control={control}
							ignoreUserId={user?.id ?? ""}
						/>
						<FormSection title="Budget">
							<div className="space-y-4">
								<CurrencyInput name="amountCents"
											   useControllerError={true}
											   control={control} />
							</div>
						</FormSection>
						<QuotaConsumersFormSection
							control={control}
							fields={fields}
							onAddClick={() => setShowOrderSelect(true)}
							onRemove={remove}
							orderInfoById={orderInfoById}
							showEmptyError={isSubmitted && fields.length === 0}
						/>
						<FormSection title="Rechnungsdetails">
							<div className="space-y-4">
								{requiresInvoiceProject &&
									<div>
										<Select name="invoiceProjectId"
												control={control}
												label="Projekt"
												placeholder="Bitte Projekt für Rechnung auswählen"
												optionsData={invoiceProjectOptions} />
										<div className="text-sm text-gray-500">
											Eine Projektrechnung muss immer genau einem Projekt zugeordnet sein. Die
											ausgewählten Bestellungen gehören zu mehreren Projekten, daher muss hier ein
											Projekt für die Rechnung ausgewählt werden.
										</div>
									</div>
								}
								<Input name="orderNumber"
									   control={control}
									   label="Bestellnummer" />
								<div className="grid grid-cols-2 gap-x-4">
									<DatePickerInput name="invoiceDeliveryStartDate"
													 control={control}
													 clearable={false}
													 label="Leistungszeitraum Start" />
									<DatePickerInput name="invoiceDeliveryEndDate"
													 control={control}
													 clearable={false}
													 label="Leistungszeitraum Ende" />
								</div>
								<BillingInvoiceRecipientFormSection
									control={control}
									clientContactPersonSelectOptions={companiesContactPersonSelectOptions}
									isSubmitted={isSubmitted}
									// @ts-expect-error - needs to be fixed
									errorMessage={errors[""]?.message}
									invoiceRecipientSelectOptions={companiesInvoiceRecipientSelectOptions}
									setValue={setValue}
									trigger={trigger}
									watch={watch}
								/>
							</div>
							<PaymentTargetDaysInput
								control={control}
								showCustomPaymentTargetInput={showCustomPaymentTargetInput}
							/>
						</FormSection>
						<FormSection title="Anmerkungen">
							<Textarea name="comment"
									  control={control} />
						</FormSection>
					</FormSectionGroup>
				</SidebarContent>
				<SidebarFooter>
					<FormHasErrorsHint show={isSubmitted && (!isValid || fields.length === 0)}
									   className="mr-2" />
					<SubmitButton busy={isBusy}
								  disabled={!formIsSubmittable || (isSubmitted && fields.length === 0)}>
						Speichern
					</SubmitButton>
					<Button theme="white"
							onClick={onCancel}>
						Abbrechen
					</Button>
				</SidebarFooter>
			</>}

		</form>);
};
